import styled from '@emotion/styled';
import { FC, PropsWithChildren, ReactNode } from 'react';

import Container from 'app/components/layout/Container';
import Page, { PageProps } from 'app/components/layout/Page';
import FourOhFour from 'app/pages/error/FourOhFour';
import { useGeneralState } from 'app/state/GeneralContext/hooks';

import Flex from '../../components/Flex';
import Heading3 from '../../components/Text/Heading3';

// Local Typings
interface Props extends PageProps {
  asideContent?: ReactNode;
  mainContent: ReactNode;
  notFound?: boolean;
  secondaryContent?: ReactNode;
  withTitle?: boolean;
}

// Local Variables
const asideMinWidth = 350;
const containerMaxWidth = 752;

export const showPageContainerClassName = 'show-page-container';
export const showPageAsideClassName = 'show-page-aside';

export const ShowPageContainer = styled(Flex)(({ theme }) => ({
  alignItems: 'initial',
  flexWrap: 'wrap',
  gap: theme.spacing * 4,
  width: '100%',

  [theme.components.mediaQueries.lg.min]: {
    flexWrap: 'nowrap',

    [`& .${showPageContainerClassName}`]: {
      maxWidth: containerMaxWidth,
    },

    [`& .${showPageAsideClassName}`]: {
      maxWidth: asideMinWidth + 100,
      minWidth: asideMinWidth,
    },
  },
}));

// Component Definition
const ShowPage: FC<PropsWithChildren<Props>> = ({
  asideContent,
  children,
  mainContent,
  notFound,
  secondaryContent,
  withTitle,
  ...pageProps
}) => {
  const {
    currentDevice,
  } = useGeneralState();

  const isMobileWidth = currentDevice === 'mobile';

  return notFound
    ? (
      <FourOhFour />
    )
    : (
      <Page
        {...pageProps}
        skeletonType="show"
      >
        <Container>
          {withTitle && (
            <Heading3
              fontWeight="normal"
              marginBottom={4}
            >
              {pageProps.title}
            </Heading3>
          )}

          <ShowPageContainer>
            <Flex
              alignItems="initial"
              className={showPageContainerClassName}
              flexDirection="column"
              fullWidth
              gap={4}
              height="min-content"
            >
              {mainContent}

              {secondaryContent}
            </Flex>

            {asideContent && (
              <Flex
                alignItems="initial"
                className={showPageAsideClassName}
                flexDirection="column"
                flexGrow={1}
                fullWidth={isMobileWidth}
                gap={4}
                height="min-content"
              >
                {asideContent}
              </Flex>
            )}
          </ShowPageContainer>

          {children}
        </Container>
      </Page>
    );
};
export default ShowPage;
